import type { ComponentProps, ReactNode } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import type { Rechenbasis, Posten } from "./kern/rechnen";
import type { Pruefpunkt } from "./kern/pruefliste";
import type { Daten } from "./daten";

export type TabProps = {
  jahr: number;
  daten: Daten;
  basis: Rechenbasis;
  posten: Posten[];
  punkte: Pruefpunkt[];
  neuLaden: () => Promise<unknown>;
};

const EURO = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
});
export const fmtCent = (c: number) => EURO.format(c / 100);
export const fmtGanz = (e: number) => `${e.toLocaleString("de-DE")} €`;
export const fmtDatum = (iso: string | null) => {
  if (!iso) return "";
  const [j, m, t] = iso.split("-");
  return `${t}.${m}.${j}`;
};
export const heute = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const zahlLesen = (t: string) =>
  Number(t.replace(/\./g, "").replace(",", ".")) || 0;

export const Abschnitt = ({
  titel,
  text,
  children,
}: {
  titel: string;
  text?: ReactNode;
  children: ReactNode;
}) => (
  <Card>
    <CardContent className="space-y-3">
      <div>
        <h2 className="font-semibold">{titel}</h2>
        {text && <p className="text-sm text-muted-foreground">{text}</p>}
      </div>
      {children}
    </CardContent>
  </Card>
);

export const Feld = ({
  name,
  className,
  children,
}: {
  name: string;
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn("space-y-1", className)}>
    <Label className="text-xs text-muted-foreground">{name}</Label>
    {children}
  </div>
);

export const Auswahl = ({ className, ...props }: ComponentProps<"select">) => (
  <select
    className={cn(
      "border-input bg-background h-9 w-full rounded-md border px-2 text-sm shadow-xs",
      className,
    )}
    {...props}
  />
);

export const Tabelle = ({
  kopf,
  children,
  rechts = [],
}: {
  kopf: string[];
  children: ReactNode;
  rechts?: number[];
}) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-left text-xs text-muted-foreground">
          {kopf.map((k, i) => (
            <th
              key={i}
              className={cn(
                "px-2 py-1.5 font-medium whitespace-nowrap",
                rechts.includes(i) && "text-right",
              )}
            >
              {k}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="[&_td]:px-2 [&_td]:py-1.5 [&_tr]:border-b [&_tr:last-child]:border-0">
        {children}
      </tbody>
    </table>
  </div>
);

export const Zahl = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <td className={cn("text-right tabular-nums whitespace-nowrap", className)}>
    {children}
  </td>
);

export const Hinweis = ({
  art = "info",
  children,
}: {
  art?: "info" | "warnung";
  children: ReactNode;
}) => (
  <div
    className={cn(
      "rounded-md border px-3 py-2 text-sm",
      art === "warnung"
        ? "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
        : "bg-muted text-muted-foreground",
    )}
  >
    {children}
  </div>
);
